import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';
import { IconChevronRight } from '@tabler/icons-react';
import { refreshToken } from 'aws-auth-lib';
import Breadcrumbs from '../../component/extended/Breadcrumbs';
import { useRole } from '../../utils/RoleContext';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

const Main = styled('main', {
    shouldForwardProp: (prop) => prop !== 'open',
    label: 'main'
})(({ theme, open }) => ({
    ...theme.typography.mainContent,
    marginTop: '0px',
    padding: '0px',
    marginRight: '0px',
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

function MainLayout() {
    const theme = useTheme();
    const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();
    const { role } = useRole();

    const handleLeftDrawerToggle = () => {
        dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
    };

    useEffect(() => {
        dispatch({ type: SET_MENU, opened: !matchDownMd });

        refreshToken().then((token) => {
            const jwt = token.toString();
            sessionStorage.setItem('token', jwt);
        });
    }, [dispatch, matchDownMd]);

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {role !== 'PATIENT' && (
                <Sidebar
                    drawerOpen={leftDrawerOpened}
                    drawerToggle={handleLeftDrawerToggle}
                />
            )}
            <Main open={leftDrawerOpened}>
                <AppBar
                    position="sticky"
                    sx={{
                        bgcolor: '#FDFCFF' // heme.palette.background.default
                    }}
                >
                    <Toolbar sx={{ paddingTop: 0 }}>
                        <Header
                            handleLeftDrawerToggle={handleLeftDrawerToggle}
                            open={leftDrawerOpened}
                        />
                    </Toolbar>
                </AppBar>
                <Breadcrumbs
                    separator={IconChevronRight}
                    navigation={navigation(role)}
                    icon
                    title
                    rightAlign
                />
                <Outlet />
            </Main>
        </Box>
    );
}

export default MainLayout;
